import React, { useMemo } from 'react';
import { renderInvoiceHtml } from '../../lib/invoice-template';

// Sprint I C6 — embed the canonical invoice HTML template inline. The same
// renderer feeds the PDF generator, so the on-screen view matches the PDF
// pixel-for-pixel. We isolate styles via an iframe srcDoc so the template's
// inline CSS doesn't bleed into the dark admin/portal chrome.
//
// Props:
//   invoice      — invoice row (snake_case fields from API; signature_required,
//                  amount_cents, etc.)
//   lineItems    — [{ description, qty, unit_cost_cents, amount_cents }, …]
//   account      — { name, type_school_dbn, type_school_address, … }
//   signature    — { signature_type, signature_data, font_family, signed_at } | null
//   height       — iframe height in px (default 900)
export default function InvoiceTemplateFrame({
  invoice,
  lineItems = [],
  account = {},
  signature = null,
  height = 900,
}) {
  const html = useMemo(() => {
    if (!invoice) return '';
    try {
      return renderInvoiceHtml(invoice, lineItems, account, signature);
    } catch (err) {
      return `<pre style="color:#a00;padding:24px;font-family:monospace;">${String(err?.message || err)}</pre>`;
    }
  }, [invoice, lineItems, account, signature]);

  if (!invoice) return null;
  // Sandbox notes: sandbox="" was blocking inline data: URLs (signature PNG)
  // from rendering in some browser configs. allow-same-origin keeps script
  // execution off (no allow-scripts) while letting <img src="data:..."> work.
  // The template HTML is generated by us with escapeHtml-applied fields;
  // there is no path for untrusted script content to land here.
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white overflow-hidden" data-testid="invoice-template-frame">
      <iframe
        title={`Invoice ${invoice.invoice_number || invoice.invoiceNumber || ''}`}
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{ width: '100%', height: `${height}px`, border: 0, background: '#fff' }}
      />
    </div>
  );
}
